import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../../context/DarkModeContext';
import { 
  CogIcon,
  PencilIcon,
  ArchiveBoxIcon,
  PlayIcon,
  TrashIcon,
  StarIcon,
  AcademicCapIcon,
  ArrowRightIcon,
  EllipsisVerticalIcon,
  Squares2X2Icon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';
import { FocusModeSlider } from '../CareerFocus/FocusModeSlider';
import FocusSwitcher from '../FocusHub/FocusSwitcher';
import { FOCUS_TYPES } from '../../constants/focusTypes';
import { useSetDefaultFocus } from '../../hooks/useDefaultFocus';
import { useDeactivateCareerFocus, useDeleteCareerFocus } from '../../hooks/useCareerFocus';
import { useToast } from '../../context/ToastContext';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../context/AuthContext';
import { useDataCache } from '../../context/DataCacheContext';
import { CACHE_KEYS } from '../../constants/cacheKeys';
import EditFocusModal from './EditFocusModal';

interface StandardFocusHeaderProps {
  focus: any; // CareerFocusWithMetrics
  title?: string; // Override default title
  mobileTitle?: string; // Shorter title for mobile
  subtitle?: string; // Optional subtitle (e.g., company • role)
  actionButton?: {
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    variant?: 'primary' | 'secondary';
  };
  isCondensed?: boolean;
  condensedContent?: React.ReactNode; // Optional content to show when condensed (e.g., progress bar)
  onStartTour?: () => void; // For showing tutorial
  isDefaultFocus?: boolean; // Whether this focus is the default
}

const StandardFocusHeader: React.FC<StandardFocusHeaderProps> = ({
  focus,
  title,
  mobileTitle,
  subtitle,
  actionButton,
  isCondensed = false,
  condensedContent,
  onStartTour,
  isDefaultFocus = false
}) => {
  const navigate = useNavigate();
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const { user } = useAuth();
  const { invalidateCache } = useDataCache();
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const { setDefaultFocus, isSetting } = useSetDefaultFocus();
  const { deactivateFocus, isDeactivating } = useDeactivateCareerFocus();
  const { deleteFocus, isDeleting } = useDeleteCareerFocus();

  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';

  // Get focus type configuration
  const focusConfig = FOCUS_TYPES[focus.focus_type] || FOCUS_TYPES.internship;
  const displayTitle = title || focusConfig.label;

  // Build subtitle from focus data if not provided
  const displaySubtitle = subtitle || [
    focus.company,
    focus.role
  ].filter(Boolean).join(' • ');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSettingsDropdown(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setShowMobileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleEdit = () => {
    setShowEditModal(true);
    setShowSettingsDropdown(false);
  };

  const handleSetDefault = async () => {
    const success = await setDefaultFocus(focus.id);
    if (success) {
      showToast('Default focus updated', 'success');
    }
    setShowSettingsDropdown(false);
  };

  const handleActivate = async () => {
    if (!user?.id) {
      showToast('You must be logged in', 'error');
      return;
    }

    setIsActivating(true);
    try {
      const { error } = await supabase
        .from('user_career_focuses')
        .update({ is_active: true })
        .eq('id', focus.id)
        .eq('user_id', user.id);

      if (error) throw error;

      showToast(`${focusConfig.label} activated successfully`, 'success');
      
      // Invalidate caches
      await invalidateCache(`${CACHE_KEYS.CAREER_FOCUS_ACTIVE}_${user.id}`);
      await invalidateCache(`${CACHE_KEYS.ALL_ACTIVE_FOCUSES}_${user.id}`);
      await invalidateCache(`${CACHE_KEYS.COMPLETED_FOCUSES}_${user.id}`);
      
      // Refresh the page to show updated status
      window.location.reload();
    } catch (error) {
      console.error('Failed to activate focus:', error);
      showToast('Failed to activate focus', 'error');
    } finally {
      setIsActivating(false);
      setShowSettingsDropdown(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      await deactivateFocus(focus.id);
      showToast(`${focusConfig.label} deactivated successfully`, 'success');
      setShowSettingsDropdown(false);
      navigate('/focus');
    } catch (error) {
      console.error('Failed to deactivate focus:', error);
      showToast('Failed to deactivate focus', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteFocus(focus.id);
      showToast(`${focusConfig.label} deleted successfully`, 'success');
      navigate('/focus');
    } catch (error) {
      console.error('Failed to delete focus:', error);
      showToast('Failed to delete focus', 'error');
    }
  };

  return (
    <div className={`sticky top-0 z-40 ${bgColor} ${isCondensed ? 'shadow-lg' : ''}`}>
      <header className={`${borderColor} border-b transition-all duration-300`}>
        <div className="px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-2">
            {/* Left section - Title and Switcher */}
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1 mr-2 pl-10 lg:pl-0">
              {/* Mobile: Show only the switcher */}
              <div className="sm:hidden flex items-center h-full">
                <FocusSwitcher currentFocus={focus} showBackButton={false} />
              </div>
              
              {/* Desktop: Show title and switcher */}
              <div className="hidden sm:flex items-center gap-4 min-w-0 flex-1">
                <div className="min-w-0 flex-shrink">
                  <h1 className={`text-xl font-bold ${textColor} truncate`}>
                    {displayTitle}
                  </h1>
                  <AnimatePresence>
                    {!isCondensed && displaySubtitle && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className={`${mutedTextColor} text-sm truncate`}
                      >
                        {displaySubtitle}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                
                <FocusSwitcher currentFocus={focus} showBackButton={false} />
              </div>
            </div>

            {/* Right section - Hub button, Toggle, Action, Settings */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* Hub button - always visible */}
              <button
                onClick={() => navigate('/focus')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Focus Hub"
                title="Back to Focus Hub"
              >
                <Squares2X2Icon className="w-5 h-5" />
              </button>
              
              {/* Desktop: Show all items */}
              <div className="hidden sm:block">
                <FocusModeSlider />
              </div>
              
              <div className="hidden sm:block h-8 w-px bg-gray-300 dark:bg-gray-600" />
              
              {/* Action button - visible on all screens but icon-only on mobile */}
              {actionButton && (
                <button
                  onClick={actionButton.onClick}
                  className={`quick-add-btn flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-colors text-sm font-medium ${
                    actionButton.variant === 'primary'
                      ? 'bg-purple-600 hover:bg-purple-700 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {actionButton.icon}
                  <span className="hidden sm:inline">{actionButton.label}</span>
                </button>
              )}
              
              {/* Desktop: Settings Dropdown */}
              <div className="hidden sm:block relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="Settings"
                >
                  <CogIcon className="w-5 h-5" />
                </button>
                
                {/* Dropdown Menu */}
                <AnimatePresence>
                  {showSettingsDropdown && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.1 }}
                      className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50"
                    >
                      {/* Tutorial (if available) */}
                      {onStartTour && (
                        <>
                          <button
                            onClick={() => {
                              onStartTour();
                              setShowSettingsDropdown(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <AcademicCapIcon className="w-5 h-5 text-purple-600" />
                            <span className="text-sm font-medium">View Tutorial</span>
                          </button>
                          <div className="h-px bg-gray-200 dark:bg-gray-700 my-2" />
                        </>
                      )}
                      
                      {/* Set as Default */}
                      {focus.is_active && (
                        <button
                          onClick={handleSetDefault}
                          disabled={isSetting || isDefaultFocus}
                          className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                        >
                          {isDefaultFocus ? (
                            <StarIconSolid className="w-5 h-5 text-yellow-500" />
                          ) : (
                            <StarIcon className="w-5 h-5 text-gray-500" />
                          )}
                          <span className="text-sm font-medium">
                            {isDefaultFocus ? 'Default Focus' : 'Set as Default'}
                          </span>
                        </button>
                      )}
                      
                      {/* Edit Focus */}
                      <button
                        onClick={handleEdit}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <PencilIcon className="w-5 h-5 text-gray-500" />
                        <span className="text-sm font-medium">Edit Focus</span>
                      </button>
                      
                      {/* Activate/Deactivate */}
                      {focus.is_active ? (
                        <button
                          onClick={handleDeactivate}
                          disabled={isDeactivating}
                          className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <ArchiveBoxIcon className="w-5 h-5 text-yellow-600" />
                          <span className="text-sm font-medium">
                            {isDeactivating ? 'Deactivating...' : 'Deactivate Focus'}
                          </span>
                        </button>
                      ) : (
                        <button
                          onClick={handleActivate}
                          disabled={isActivating}
                          className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <PlayIcon className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium">
                            {isActivating ? 'Activating...' : 'Activate Focus'}
                          </span>
                        </button>
                      )}
                      
                      <div className="h-px bg-gray-200 dark:bg-gray-700 my-2" />
                      
                      {/* Delete */}
                      {!showDeleteConfirm ? (
                        <button
                          onClick={() => setShowDeleteConfirm(true)}
                          className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <TrashIcon className="w-5 h-5 text-red-600" />
                          <span className="text-sm font-medium text-red-600">Delete Focus</span>
                        </button>
                      ) : (
                        <div className="px-4 py-2">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            Delete this focus permanently?
                          </p>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowDeleteConfirm(false)}
                              className="flex-1 px-3 py-1 text-sm bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleDelete}
                              disabled={isDeleting}
                              className="flex-1 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                            >
                              {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              {/* Mobile: Menu button */}
              <div className="sm:hidden relative" ref={mobileMenuRef}>
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="Menu"
                >
                  <EllipsisVerticalIcon className="w-5 h-5" />
                </button>
                
                {/* Mobile Dropdown Menu */}
                <AnimatePresence>
                  {showMobileMenu && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.1 }}
                      className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50"
                    >
                      {/* Focus Mode Toggle */}
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <FocusModeSlider />
                      </div>
                      
                      {/* Tutorial (if available) */}
                      {onStartTour && (
                        <>
                          <button
                            onClick={() => {
                              onStartTour();
                              setShowMobileMenu(false);
                            }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <AcademicCapIcon className="w-5 h-5 text-purple-600" />
                            <span className="text-sm font-medium">View Tutorial</span>
                          </button>
                          <div className="h-px bg-gray-200 dark:bg-gray-700 my-2" />
                        </>
                      )}
                      
                      {/* Settings options */}
                      {focus.is_active && (
                        <button
                          onClick={() => {
                            handleSetDefault();
                            setShowMobileMenu(false);
                          }}
                          disabled={isSetting || isDefaultFocus}
                          className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                        >
                          {isDefaultFocus ? (
                            <StarIconSolid className="w-5 h-5 text-yellow-500" />
                          ) : (
                            <StarIcon className="w-5 h-5 text-gray-500" />
                          )}
                          <span className="text-sm font-medium">
                            {isDefaultFocus ? 'Default Focus' : 'Set as Default'}
                          </span>
                        </button>
                      )}
                      
                      <button
                        onClick={() => {
                          setShowEditModal(true);
                          setShowMobileMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <PencilIcon className="w-5 h-5 text-gray-500" />
                        <span className="text-sm font-medium">Edit Focus</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          setShowSettingsDropdown(true);
                          setShowMobileMenu(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <CogIcon className="w-5 h-5 text-gray-500" />
                        <span className="text-sm font-medium">More Settings</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Condensed content (e.g., progress bar) */}
      <AnimatePresence>
        {isCondensed && condensedContent && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            {condensedContent}
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Edit Focus Modal */}
      <EditFocusModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        focus={focus}
      />
    </div>
  );
};

export default StandardFocusHeader;